import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof AlertDialogPrimitive.Overlay> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref as React.Ref<HTMLDivElement>}
    className={cn(
      "fixed inset-0 z-50 bg-hero/55 dark:bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof AlertDialogPrimitive.Content> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-32px)] max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 rounded-card border border-border bg-surface p-6 text-text shadow-pop duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-[2%] data-[state=open]:slide-in-from-bottom-[2%]",
        className
      )}
      {...props} />
  </AlertDialogPortal>
))
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { className?: string }) => (
  <div
    className={cn("flex flex-col gap-1.5 text-left", className)}
    {...props} />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { className?: string }) => (
  <div
    className={cn("flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end", className)}
    {...props} />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef<HTMLHeadingElement, React.ComponentProps<typeof AlertDialogPrimitive.Title> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title ref={ref as React.Ref<HTMLHeadingElement>} className={cn("font-display text-xl font-extrabold leading-tight", className)} {...props} />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef<HTMLParagraphElement, React.ComponentProps<typeof AlertDialogPrimitive.Description> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref as React.Ref<HTMLParagraphElement>}
    className={cn("text-sm text-text-2", className)}
    {...props} />
))
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof AlertDialogPrimitive.Action> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref as React.Ref<HTMLButtonElement>} className={cn(buttonVariants(), className)} {...props} />
))
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof AlertDialogPrimitive.Cancel> & { className?: string }>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref as React.Ref<HTMLButtonElement>}
    className={cn(buttonVariants({ variant: "secondary" }), className)}
    {...props} />
))
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
